import {
    DatasetDto,
    DatasetReferenceDto,
    DatasetReferenceType,
    DatasetType,
    NMOC_MANAGED_SIMULATION_DTO_CLAZZ_VALUE,
    NmocManagedSimulationDto,
    SimulationDto,
    SimulationIdentifierDto,
    SimulationType,
    STANDALONE_SIMEX_DTO_CLAZZ_VALUE,
    StandaloneSimexDto,
    USER_MANAGED_SIMULATION_DTO_CLAZZ_VALUE,
    UserManagedSimulationDto,
} from 'app/model/CORE';
import KeyUtils from 'app/common/util/KeyUtils';

export class SimulationUtils {
    static KNOWN_SIMULATIONS: SimulationDto[];

    static getKnownSimulation(simulId: SimulationIdentifierDto) {
        return (
            simulId &&
            KeyUtils.getValueFromKey(SimulationUtils.KNOWN_SIMULATIONS, simulId, KeyUtils.SIMULATION_KEY_PROVIDER)
        );
    }

    static isUserManagedSimulation(sim: SimulationDto): sim is UserManagedSimulationDto {
        return sim.clazz === USER_MANAGED_SIMULATION_DTO_CLAZZ_VALUE;
    }

    static isNmocManagedSimulation(sim: SimulationDto): sim is NmocManagedSimulationDto {
        return sim.clazz === NMOC_MANAGED_SIMULATION_DTO_CLAZZ_VALUE;
    }

    static isStandaloneSimex(sim: SimulationDto): sim is StandaloneSimexDto {
        return sim.clazz === STANDALONE_SIMEX_DTO_CLAZZ_VALUE;
    }

    static addKnownSimulation(sim: SimulationDto) {
        if (!this.KNOWN_SIMULATIONS) {
            this.KNOWN_SIMULATIONS = [sim];
        } else {
            this.KNOWN_SIMULATIONS = [...this.KNOWN_SIMULATIONS, sim];
        }
    }

    static getDatasetReference(sim: SimulationDto) {
        if (SimulationUtils.isUserManagedSimulation(sim)) return sim.datasetReference;
        if (SimulationUtils.isNmocManagedSimulation(sim)) return sim.datasetReference;
        if (SimulationUtils.isStandaloneSimex(sim)) return null;
    }

    static getDatasetDerivedFrom(sim: SimulationDto): DatasetDto {
        const ref = SimulationUtils.getDatasetReference(sim);
        if (ref) {
            switch (ref.type) {
                case DatasetReferenceType.FORECAST:
                    return { type: DatasetType.FORECAST };
                case DatasetReferenceType.OPERATIONAL:
                    return { type: DatasetType.OPERATIONAL };
                case DatasetReferenceType.STANDALONE_SIMEX:
                    return {
                        type: DatasetType.SIMULATION,
                        simulIdentifier: { type: SimulationType.STANDALONE_SIMEX, id: ref.simulationId },
                    };
            }
        }
    }

    static getDatasetReferenceFrom(dataset: DatasetDto): DatasetReferenceDto {
        switch (dataset.type) {
            case DatasetType.OPERATIONAL:
                return { type: DatasetReferenceType.OPERATIONAL };
            case DatasetType.FORECAST:
                return { type: DatasetReferenceType.FORECAST };
            case DatasetType.SIMULATION:
                return { type: DatasetReferenceType.STANDALONE_SIMEX, simulationId: dataset.simulIdentifier.id };
        }
    }

    static getSimulationTypeString(simType: SimulationType);
    static getSimulationTypeString(sid: SimulationIdentifierDto);

    static getSimulationTypeString(param?: SimulationType | SimulationIdentifierDto) {
        const simType =
            (param as SimulationIdentifierDto).type !== undefined ? (param as SimulationIdentifierDto).type : param;
        switch (simType) {
            case SimulationType.NMOC_MANAGED_SIMULATION:
                return 'NMOC SIM';
            case SimulationType.STANDALONE_SIMEX:
                return 'SIMEX';
            case SimulationType.USER_MANAGED_SIMULATION:
                return 'USER SIM';
        }
        return '???';
    }

    static getSimIdentifierString(sid: SimulationIdentifierDto): string {
        return sid ? sid.id : null;
    }

    static getDatasetReferenceString(ref: DatasetReferenceDto): string {
        if (ref) {
            const refType = SimulationUtils.getDatasetReferenceTypeMediumString(ref.type);

            return ref.simulationId ? refType + ' [' + ref.simulationId + ']' : refType;
        }
    }

    static getDatasetReferenceTypeMediumString(type: DatasetReferenceType): string {
        switch (type) {
            case DatasetReferenceType.FORECAST:
                return 'FCAST';
            case DatasetReferenceType.OPERATIONAL:
                return 'OPS';
            case DatasetReferenceType.STANDALONE_SIMEX:
                return 'SIMEX';
        }
        return '';
    }

    static getDatasetReferenceTypeShortString(type: DatasetReferenceType): string {
        switch (type) {
            case DatasetReferenceType.FORECAST:
                return 'F';
            case DatasetReferenceType.OPERATIONAL:
                return 'O';
            case DatasetReferenceType.STANDALONE_SIMEX:
                return 'S';
        }
        return '';
    }
}
